import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Eye, EyeOff, Save, Plug, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const isPlaceholder = (v) => !v || (typeof v === "string" && (v.startsWith("***") || /^•+$/.test(v)));

export default function AdminAPIMaster() {
  const [rows, setRows] = useState([]);
  const [showKey, setShowKey] = useState({});
  const [edit, setEdit] = useState(null);
  const [testing, setTesting] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/suppliers"); setRows(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        endpoint: edit.endpoint || "",
        api_key: isPlaceholder(edit.api_key) ? "" : edit.api_key,
        api_secret: isPlaceholder(edit.api_secret) ? "" : edit.api_secret,
      };
      if (edit.code === "travelport") payload.branch = edit.branch || "";
      await api.patch(`/admin/suppliers/${edit.id}`, payload);
      toast.success("Credentials saved and applied at runtime");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const toggle = async (row) => {
    try {
      await api.post(`/admin/suppliers/${row.id}/toggle`);
      toast.success(`${row.name} is now ${row.active ? "inactive" : "active"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const testConn = async (row) => {
    setTesting((t) => ({ ...t, [row.id]: "running" }));
    try {
      const { data } = await api.post(`/admin/suppliers/${row.id}/test`);
      setTesting((t) => ({ ...t, [row.id]: data.ok ? "ok" : "fail" }));
      (data.ok ? toast.success : toast.error)(`${row.name}: ${data.detail}`);
    } catch (e) {
      setTesting((t) => ({ ...t, [row.id]: "fail" }));
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">API Master</h1>
      <p className="text-slate-500 mt-1">Supplier endpoints and credentials. Edit and save to apply live — secrets are masked by default.</p>

      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead>
            <TableHead>Endpoint</TableHead><TableHead>API Key</TableHead><TableHead>API Secret</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(r => {
              const t = testing[r.id];
              return (
                <TableRow key={r.id} data-testid={`apim-row-${r.id}`}>
                  <TableCell className="font-mono">{r.code}</TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell><Badge className="bg-teal-50 text-teal-700 border-0 capitalize">{r.type}</Badge></TableCell>
                  <TableCell className="text-xs font-mono max-w-xs truncate">{r.endpoint || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">
                    {r.api_key ? (showKey[r.id] ? r.api_key : "•".repeat(Math.min(12, (r.api_key || "").length))) : "—"}
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {r.api_secret ? (showKey[r.id] ? r.api_secret : "•".repeat(Math.min(10, (r.api_secret || "").length))) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`apim-active-${r.code}`}
                        checked={!!r.active}
                        onCheckedChange={() => toggle(r)}
                      />
                      <span className={"text-[11px] font-medium " + (r.active ? "text-emerald-700" : "text-red-600")}>
                        {r.active ? "active" : "inactive"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setShowKey({ ...showKey, [r.id]: !showKey[r.id] })} title="Show / hide secrets">
                        {showKey[r.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </Button>
                      {["amadeus", "travelport", "ndcx"].includes(r.code) && (
                        <Button size="sm" variant="ghost" onClick={() => testConn(r)} data-testid={`apim-test-${r.code}`} title="Test connection">
                          {t === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                           t === "ok" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> :
                           t === "fail" ? <XCircle className="w-3.5 h-3.5 text-red-600" /> :
                           <Plug className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setEdit({ ...r })} data-testid={`apim-edit-${r.code}`}>Edit</Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">No suppliers</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit && (
          <DialogContent data-testid="apim-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">
                Edit API credentials — {edit.name}
              </DialogTitle>
              <DialogDescription>
                Update the endpoint and credentials. Changes apply to the live runtime client immediately on save.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Endpoint URL</Label>
                <Input
                  data-testid="apim-edit-endpoint"
                  value={edit.endpoint || ""}
                  onChange={e => setEdit({ ...edit, endpoint: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>{edit.code === "travelport" || edit.code === "ndcx" ? "Username" : "API Key"}</Label>
                <Input
                  data-testid="apim-edit-key"
                  value={edit.api_key || ""}
                  onChange={e => setEdit({ ...edit, api_key: e.target.value })}
                  placeholder={isPlaceholder(edit.api_key) ? "Enter to update" : ""}
                />
              </div>
              <div>
                <Label>{edit.code === "travelport" || edit.code === "ndcx" ? "Password" : "API Secret"}</Label>
                <Input
                  data-testid="apim-edit-secret"
                  type="password"
                  value={edit.api_secret || ""}
                  onChange={e => setEdit({ ...edit, api_secret: e.target.value })}
                  placeholder={isPlaceholder(edit.api_secret) ? "Enter to update" : ""}
                />
              </div>
              {edit.code === "travelport" && (
                <div>
                  <Label>Target Branch (PCC)</Label>
                  <Input
                    data-testid="apim-edit-branch"
                    value={edit.branch || ""}
                    onChange={e => setEdit({ ...edit, branch: e.target.value })}
                    placeholder="e.g. P3738655927"
                  />
                </div>
              )}
              <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md">
                Credentials are stored in MongoDB and pushed to the live runtime client immediately. They take precedence over <code className="font-mono">backend/.env</code> values.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button data-testid="apim-save" onClick={save} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
                <Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save & apply"}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
